import React, { useCallback, useEffect, useMemo, useState } from "react";
import ChoosePlatforms from "../../components/campaign/ChoosePlatforms";
import StepProgressBar from "../../components/progress-bar";
import ChooseFrequency from "../../components/campaign/ChooseFrequency";
import ChooseCampaignOptions from "../../components/campaign/ChooseCampaignOptions";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
    CampaignPlatform,
    CampaignSettingsProps,
    ContentLength,
    LocationState,
    SocialPlatforms,
    SocialsConnected,
    PROMOTION_DISALLOWED_PLATFORMS,
} from "../../components/campaign/types";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import { getAuthHeader } from "../../utils/authHeader";
import { getApiErrorMessage } from "../../utils/apiError";
import axios from "axios";
import LoadingScreen from "../LoadingScreen";
import { Button } from "../../components/misc/Button";
import CampaignGenerationMaintenance from "../../components/campaign/CampaignGenerationMaintenance";
import { ListingType } from "../../types/userListing";
import { useAutopilotEnabled } from "../../hooks/useAutopilotEnabled";
import { AutopilotResponse } from "../../types/autopilot";
import { useIsMobile } from "../../hooks/useIsMobile";
import { DAYS_OF_WEEK } from "../../utils/daysOfWeek";
// MB-1454: null-safe schedule helpers (extracted; poison postTimes skipped).
import {
    formatTime,
    getOccupiedSlotsByPlatform,
} from "../../utils/campaignSchedule";
import {
    excludeCampaign,
    instagramPostsPerWeek,
    INSTAGRAM_WEEKLY_POST_LIMIT,
} from "../../utils/instagramLimit";
import {
    autopilotOwnsInstagram,
    shouldDeriveAutopilotPromo,
} from "../../utils/promotionWizardMode";
import { isSharedListing } from "../../utils/sharedListingFooter";
import PlanLimitReached, {
    PlanLimitType,
} from "../../components/misc/PlanLimitReached";

const findAvailableTimeSlot = (
    occupiedSlots: Set<string>,
    defaultTime: { hour: number; minute: number },
    days: string[]
): { hour: number; minute: number } => {
    let { hour, minute } = defaultTime;
    const MAX_HOUR = 23;
    while (true) {
        const timeStr = `${hour.toString().padStart(2, "0")}:${minute
            .toString()
            .padStart(2, "0")}`;
        // Check conflict only in the given days (per platform)
        const conflict = days.some((day) =>
            occupiedSlots.has(`${day}-${timeStr}`)
        );
        if (!conflict) {
            return { hour, minute };
        }
        // Shift time by 30 minutes.
        minute += 30;
        if (minute >= 60) {
            minute %= 60;
            hour++;
        }
        if (hour > MAX_HOUR) {
            // Wrap around to beginning of the day.
            hour = 0;
        }
    }
};

const updateOccupiedSlots = (
    occupiedSlots: Set<string>,
    platform: CampaignPlatform,
    time: { hour: number; minute: number }
): void => {
    const timeStr = formatTime(time);
    if (timeStr === null) return;
    const days =
        platform.frequency === "Daily"
            ? DAYS_OF_WEEK
            : platform.dayOfWeek || [];
    days.forEach((day) => {
        occupiedSlots.add(`${day}-${timeStr}`);
    });
};

const CreateCampaign = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // MB-1436: Autopilot creates this listing's campaign itself. Hiding the
    // button is not enough - a bookmark, a stale tab or browser history still
    // lands here, and the user would fill in the whole wizard only to be
    // refused at submit.
    const { autopilotEnabled } = useAutopilotEnabled();

    const [loading, setLoading] = useState<boolean>(true);

    const [isOnline, setIsOnline] = useState<boolean>(false);

    const [campaignSettings, setCampaignSettigns] =
        useState<CampaignSettingsProps[]>();

    const [currentStep, setCurrentStep] = useState<number>(1);
    // MB-1405: promotion created while Autopilot is on — channels preselect
    // from Autopilot's selection and the manual schedule step is skipped
    // (the api derives the schedule).
    const [autopilotPromo, setAutopilotPromo] = useState<boolean>(false);
    const [selectedPlatforms, setSelectedPlatforms] =
        useState<SocialsConnected>({
            facebook: false,
            instagram: false,
            twitter: false,
            linkedin: false,
            pinterest: false,
            google: false,
            youtube: false,
        });

    const [date, setDate] = React.useState<Date>();

    const [campaignPlatforms, setCampaignPlatforms] = useState<
        CampaignPlatform[]
    >([]);

    const [autoApprove, setAutoApprove] = React.useState<boolean>(true);
    const [autoGenerateSlideshow, setAutoGenerateSlideshow] =
        React.useState<boolean>(false);
    // MB-1314: auto-video is usable only once the user has set up their Listing
    // Video Settings (the flag) AND still has a logo, brand color, and headshot.
    // Otherwise the toggle is blocked with a setup CTA.
    const [autoVideoSetupCompleted, setAutoVideoSetupCompleted] =
        React.useState<boolean>(false);
    const [hasAutoVideoLogo, setHasAutoVideoLogo] =
        React.useState<boolean>(false);
    const [hasAutoVideoColor, setHasAutoVideoColor] =
        React.useState<boolean>(false);
    const authUser = useSelector((s: RootState) => s.auth.user);
    const autoVideoReady =
        autoVideoSetupCompleted &&
        hasAutoVideoLogo &&
        hasAutoVideoColor &&
        Boolean(authUser?.corporateHeadshot);
    const [firstImage, setFirstImage] = React.useState<boolean>(false);
    const [floorPlan, setFloorPlan] = React.useState<boolean>(false);
    const [cta, setCta] = React.useState<"bottom" | "top">("bottom");
    const [openHouseMonitoring, setOpenHouseMonitoring] =
        useState<boolean>(true);

    const [planLimitOpen, setPlanLimitOpen] = useState<boolean>(false);
    const [planLimitType, setPlanLimitType] = useState<PlanLimitType>("campaign");

    const listingId =
        (location.state as LocationState)?.listingId ||
        sessionStorage.getItem("createCampaign");

    const listingType =
        (location.state as LocationState)?.listingType ||
        sessionStorage.getItem("listingType");

    // MB-1371: wizard edit mode — re-entering for an EXISTING campaign.
    // Edits are staged server-side and apply at the next renewal.
    const editCampaignId =
        (location.state as LocationState & { editCampaignId?: string })
            ?.editCampaignId ||
        sessionStorage.getItem("editCampaignId") ||
        undefined;

    // MB-1405: only for promotions started AFTER Autopilot is enabled — a
    // fetch failure leaves the wizard exactly as it always was.
    useEffect(() => {
        if (!shouldDeriveAutopilotPromo(listingType, editCampaignId)) return;
        (async () => {
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const { data } = await axios.get<AutopilotResponse>(
                    `${backendUrl}/settings/autopilot`,
                    { headers }
                );
                if (!data?.settings?.enabled) return;
                setAutopilotPromo(true);
                const channels: string[] = data.settings.channels?.length
                    ? data.settings.channels
                    : data.connectedChannels || [];
                setSelectedPlatforms((prev) => {
                    if (Object.values(prev).some(Boolean)) return prev;
                    const next = { ...prev };
                    channels.forEach((channel) => {
                        const key =
                            channel.toLowerCase() as keyof SocialsConnected;
                        if (PROMOTION_DISALLOWED_PLATFORMS.includes(key))
                            return;
                        if (key in next) next[key] = true;
                    });
                    return next;
                });
            } catch {
                // Behave exactly as the manual wizard.
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listingType, editCampaignId]);

    const from =
        (location.state as LocationState)?.from ||
        sessionStorage.getItem("from");

    const returnPath =
        from || (listingType === "Promotion" ? "/promotions" : "/listings");

    const parsedFooter =
        (location.state as LocationState)?.parsedFooter ||
        sessionStorage.getItem("parsedFooter") ||
        undefined;

    const parsedGoogleFooter =
        (location.state as LocationState)?.parsedGoogleFooter ||
        sessionStorage.getItem("parsedGoogleFooter") ||
        undefined;

    // MB-1455: in edit mode the occupancy math (IG weekly limit, schedule
    // defaults, occupied-slot map) must see the OTHER campaigns only — the
    // edited campaign's own slots otherwise count against themselves.
    const otherCampaignSettings = React.useMemo(
        () => excludeCampaign(campaignSettings, editCampaignId),
        [campaignSettings, editCampaignId],
    );

    // MB-1436, widened by MB-1441: while Autopilot is on there are no manual
    // listing campaigns at all, so the CREATE path turns away for ANY listing
    // type without loading the listing. Promotions keep the wizard (the
    // autopilot promo path IS this screen, MB-1405), and an existing campaign
    // is still editable.
    useEffect(() => {
        if (autopilotEnabled !== true) return;
        if (editCampaignId) return;
        if (!listingId || listingType === ListingType.Promotion) return;
        toast.error(
            <CustomToast
                message="Auto Pilot creates listing campaigns automatically - manual campaigns are unavailable while it's on."
                type="error"
            />,
            { autoClose: 4000 },
        );
        navigate(from || "/listings", { state: { listingId } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autopilotEnabled, editCampaignId, listingId, listingType]);

    const [nextRenewalDate, setNextRenewalDate] = React.useState<
        string | null
    >(null);
    const [editPrefillPlatforms, setEditPrefillPlatforms] = React.useState<
        CampaignPlatform[] | null
    >(null);
    const [hasPendingEdit, setHasPendingEdit] = React.useState<boolean>(false);

    useEffect(() => {
        if (!editCampaignId) return;
        sessionStorage.setItem("editCampaignId", editCampaignId);
        (async () => {
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const response = await axios.get(
                    `${backendUrl}/listing/campaign/${editCampaignId}/pending-settings`,
                    { headers }
                );
                const data = response.data;
                // Layer the staged edit over the live settings rather than
                // replacing them: a pending doc written before a new field
                // existed would otherwise read back as that field's default
                // instead of the campaign's actual value.
                const source = { ...data.current, ...(data.pending ?? {}) };
                setHasPendingEdit(data.pending != null);
                setNextRenewalDate(data.nextRenewalDate ?? null);
                setEditPrefillPlatforms(source.platforms || []);
                const names = (source.platforms || []).map(
                    (pl: CampaignPlatform) => pl.name.toLowerCase()
                );
                // A legacy promotion may still carry a channel promotions no
                // longer post to; the api refuses it, so the wizard must not
                // re-select it.
                const selectable = (key: string): boolean =>
                    names.includes(key) &&
                    !(
                        listingType === "Promotion" &&
                        PROMOTION_DISALLOWED_PLATFORMS.includes(key)
                    );
                setSelectedPlatforms({
                    facebook: selectable("facebook"),
                    instagram: selectable("instagram"),
                    twitter: selectable("twitter"),
                    linkedin: selectable("linkedin"),
                    pinterest: selectable("pinterest"),
                    google: selectable("google"),
                    youtube: selectable("youtube"),
                });
                setAutoApprove(source.autoApprove ?? true);
                setOpenHouseMonitoring(source.openHouseMonitoring ?? true);
                setFirstImage(source.firstImage ?? false);
                setFloorPlan(source.floorPlan ?? false);
                setCta(source.cta === "top" ? "top" : "bottom");
                setAutoGenerateSlideshow(
                    Boolean(source.autoGenerateSlideshow)
                );
            } catch (error) {
                console.error("Failed to load campaign settings:", error);
                // Surface the API's actual reason (e.g. managed by Autopilot,
                // or Sold/Expired/Error) and return to the listing, instead of
                // stranding the user in an empty edit wizard with a generic
                // message.
                toast.error(
                    <CustomToast
                        message={getApiErrorMessage(
                            error,
                            "Failed to load campaign settings"
                        )}
                        type="error"
                    />,
                    { autoClose: 4000 }
                );
                navigate(returnPath, { state: { listingId } });
            }
        })();
        // Abandoning the wizard must not leave the key behind: it's read on
        // every mount, so a later "Create Campaign" would silently re-open as
        // an edit of this campaign. A browser reload doesn't run cleanups, so
        // refreshing mid-wizard still resumes the edit.
        return () => {
            sessionStorage.removeItem("editCampaignId");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editCampaignId]);

    const navUsableImageCount = (location.state as LocationState)
        ?.usableImageCount;

    const usableImageCount =
        navUsableImageCount ??
        (sessionStorage.getItem("usableImageCount") !== null
            ? Number(sessionStorage.getItem("usableImageCount"))
            : undefined);




    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [currentStep]);

    useEffect(() => {
        if (listingId && listingType && from) {
            // Store listingId in session storage
            sessionStorage.setItem("createCampaign", listingId);
            sessionStorage.setItem("listingType", listingType);
            sessionStorage.setItem("from", from);

            if (typeof navUsableImageCount === "number") {
                sessionStorage.setItem(
                    "usableImageCount",
                    String(navUsableImageCount)
                );
            }

            if (parsedFooter) {
                sessionStorage.setItem("parsedFooter", parsedFooter);
            }

            if (
                listingType.toLowerCase() !== "promotion" &&
                (parsedFooter === null ||
                    parsedFooter === undefined ||
                    parsedFooter?.length <= 0)
            ) {
                toast.error(
                    <CustomToast
                        message="listing information is not found. Try again from listings page."
                        type="error"
                    />,
                    {
                        autoClose: 3000,
                    }
                );
                navigate(-1);
            }
        } else {
            toast.error(
                <CustomToast
                    message="Listing Id and/or Listing Type is not found. Try again from listings page."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
            navigate(-1);
        }
    }, [
        listingId,
        listingType,
        navigate,
        from,
        parsedFooter,
        navUsableImageCount,
    ]);

    // MB-1472 follow-up: seed "Use Price in Posts" from the account's commercial
    // pricing preference instead of hardcoding true. Found in QA — a user who
    // switched "Include pricing in commercial posts" OFF still got the price in
    // manual campaigns, because that setting only ever reached autopilot
    // allocation. The api resolves the precedence (per-listing override >
    // account setting) and omits the field for residential, so `?? true` keeps
    // today's behaviour everywhere else. Same shape as the MB-1314 auto-video
    // default below: applied once, and the user can still override per campaign.
    const [commercialUsePriceDefault, setCommercialUsePriceDefault] = useState<
        boolean | undefined
    >(undefined);
    // A listing marketed for another agent: the schedule step asks the user
    // to confirm before the attribution footer is switched off.
    const [sharedListing, setSharedListing] = useState<boolean>(false);

    useEffect(() => {
        if (!listingId || listingType === "Promotion") return;
        let cancelled = false;
        (async () => {
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const { data } = await axios.get(
                    `${backendUrl}/listing/${listingId}`,
                    { headers },
                );
                if (!cancelled) {
                    setCommercialUsePriceDefault(
                        data?.commercialUsePriceDefault,
                    );
                    setSharedListing(isSharedListing(data));
                }
            } catch (error) {
                // Non-fatal: the toggle just keeps its historical default.
                console.error("Could not load the commercial pricing default", error);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [listingId, listingType]);

    useEffect(() => {
        const platforms: CampaignPlatform[] = [];
        const occupiedSlotsByPlatform =
            getOccupiedSlotsByPlatform(otherCampaignSettings);

        // Helper to add a new platform configuration.
        const addPlatform = (
            platformName: SocialPlatforms,
            defaultTime: { hour: number; minute: number },
            additional: Partial<CampaignPlatform> = {}
        ) => {
            let basePlatform: CampaignPlatform = {
                name: platformName,
                postsPerDay: 1,
                postTimes: [defaultTime], // temporary value, will adjust below.
                contentLengths:
                    platformName === SocialPlatforms.Facebook
                        ? [ContentLength.Long]
                        : platformName === SocialPlatforms.Twitter ||
                          platformName === SocialPlatforms.LinkedIn
                        ? [ContentLength.Medium]
                        : [ContentLength.Short],
                includeFooter: listingType !== "Promotion",
                footer:
                    platformName === SocialPlatforms.Instagram
                        ? ""
                        : parsedFooter || "",
                footerType:
                    platformName === SocialPlatforms.Instagram ||
                    platformName === SocialPlatforms.Twitter
                        ? "comment"
                        : "in-post",
                threads: platformName === SocialPlatforms.Twitter,
                frequency: "Weekly",
                usePrice: commercialUsePriceDefault ?? true,
                emojis: true,
                soldPost: true,
                ...additional,
            };

            // Determine which days to check.
            const daysToCheck =
                basePlatform.frequency === "Daily"
                    ? DAYS_OF_WEEK
                    : basePlatform.dayOfWeek &&
                      basePlatform.dayOfWeek.length > 0
                    ? basePlatform.dayOfWeek
                    : [];

            // Get the occupied slots set for this specific platform.
            let occSlots = occupiedSlotsByPlatform[platformName];
            if (!occSlots) {
                occSlots = new Set<string>();
                occupiedSlotsByPlatform[platformName] = occSlots;
            }

            // Find an available time for this platform.
            const availableTime = findAvailableTimeSlot(
                occSlots,
                defaultTime,
                daysToCheck
            );
            basePlatform.postTimes = [availableTime];
            updateOccupiedSlots(occSlots, basePlatform, availableTime);

            platforms.push(basePlatform);
        };

        if (selectedPlatforms.facebook) {
            addPlatform(
                SocialPlatforms.Facebook,
                { hour: 8, minute: 30 },
                {
                    frequency: "Weekly",
                    dayOfWeek: ["Monday"],
                }
            );
        }
        if (selectedPlatforms.instagram) {
            addPlatform(
                SocialPlatforms.Instagram,
                { hour: 8, minute: 30 },
                {
                    frequency: "Weekly",
                    dayOfWeek: ["Tuesday"],
                }
            );
        }
        if (selectedPlatforms.twitter) {
            addPlatform(
                SocialPlatforms.Twitter,
                { hour: 8, minute: 30 },
                {
                    frequency: "Weekly",
                    dayOfWeek: ["Wednesday"],
                }
            );
        }
        if (selectedPlatforms.linkedin) {
            addPlatform(
                SocialPlatforms.LinkedIn,
                { hour: 8, minute: 30 },
                {
                    frequency: "Weekly",
                    dayOfWeek: ["Thursday"],
                }
            );
        }
        if (selectedPlatforms.pinterest) {
            addPlatform(
                SocialPlatforms.Pinterest,
                { hour: 8, minute: 30 },
                {
                    frequency: "Weekly",
                    dayOfWeek: ["Friday"],
                }
            );
        }
        if (selectedPlatforms.google) {
            addPlatform(
                SocialPlatforms.Google,
                { hour: 8, minute: 30 },
                {
                    frequency: "Weekly",
                    // MB-1453: Google Business gets one post on the campaign's
                    // first day at postTimes[0] - calculatePosts never reads the
                    // weekday, and the wizard no longer offers one. Send none
                    // rather than a default nobody honours. frequency still goes
                    // because the campaign schema requires it.
                    dayOfWeek: [],
                    footer: parsedGoogleFooter,
                }
            );
        }
        if (selectedPlatforms.youtube) {
            addPlatform(
                SocialPlatforms.YouTube,
                { hour: 8, minute: 30 },
                {
                    frequency: "Weekly",
                    dayOfWeek: ["Saturday"],
                }
            );
        }
        const merged = editPrefillPlatforms
            ? platforms.map(
                  (pl) =>
                      editPrefillPlatforms.find(
                          (ep) => ep.name === pl.name
                      ) ?? pl
              )
            : platforms;
        setCampaignPlatforms(merged);
    }, [
        selectedPlatforms,
        parsedFooter,
        parsedGoogleFooter,
        listingType,
        otherCampaignSettings,
        editPrefillPlatforms,
        commercialUsePriceDefault,
    ]);

    // MB-1314: default the per-campaign auto-video toggle from the user's
    // Listing Video Settings (runs once; the user can override per campaign).
    const loadAutoVideoDefault = useCallback(async () => {
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const { data } = await axios.get(
                `${backendUrl}/settings/branding`,
                { headers }
            );
            setHasAutoVideoLogo(Boolean(data?.autoVideoLogo));
            setHasAutoVideoColor(Boolean(data?.autoVideoColor));
            setAutoVideoSetupCompleted(Boolean(data?.autoVideoSetupCompleted));
            // In edit mode the campaign's own saved value wins — the global
            // default would race the prefill and overwrite what the user set
            // on this campaign. The readiness flags above still apply.
            if (!editCampaignId) {
                setAutoGenerateSlideshow(
                    Boolean(data?.autoGenerateSlideshow)
                );
            }
        } catch {
            // Non-fatal — fall back to the off default.
        }
    }, [editCampaignId]);

    useEffect(() => {
        loadAutoVideoDefault();
    }, [loadAutoVideoDefault]);

    // Force the toggle off whenever auto-video isn't set up (no logo/headshot),
    // so it can't be submitted enabled — the toggle itself is also disabled below.
    useEffect(() => {
        if (!autoVideoReady) setAutoGenerateSlideshow(false);
    }, [autoVideoReady]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const isPromotion = listingType?.toLowerCase() === "promotion";

            try {
                const requests: Promise<any>[] = [
                    axios.get(`${backendUrl}/listing/campaign-check`, { headers }),
                    axios.get(`${backendUrl}/post/campaign-settings`, { headers }),
                ];
                const [statusResponse, settingsResponse] = await Promise.all(requests);

                if (statusResponse.status === 200) {
                    // Maintenance mode only affects AI campaign generation; promotions are always available
                    setIsOnline(isPromotion ? true : statusResponse.data);
                }
                if (settingsResponse.status === 200) {
                    setCampaignSettigns(settingsResponse.data);
                }
            } catch (error: unknown) {
                console.error(
                    "Failed to fetch campaign status or settings:",
                    error
                );
                setIsOnline(false);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [listingId, listingType]);

    const countInstagramPosts = useMemo(
        () => instagramPostsPerWeek(otherCampaignSettings),
        [otherCampaignSettings],
    );

    // MB-1409: the account-wide IG weekly limit predates Autopilot and counts
    // the autopilot fleet's own posts (Cruise plans IG at 6/week by design),
    // so it would permanently exclude Instagram from autopilot-derived promos.
    // The server owns IG safety on that path (1/week promo cap + weekly
    // ceiling sweep with the promo as junior); the manual flow keeps the gate.
    const autopilotGovernsInstagram = autopilotOwnsInstagram(
        autopilotPromo,
        autopilotEnabled,
        listingType,
    );
    const instagramDisabled =
        !autopilotGovernsInstagram &&
        countInstagramPosts >= INSTAGRAM_WEEKLY_POST_LIMIT;
    const youtubeDisabled = false;

    let progressSteps = [
        { label: "Select Platforms", isActive: true, isCompleted: false },
        // MB-1405: Autopilot owns promotion scheduling — no schedule step.
        // MB-1421: no campaign-config step either — for a promotion it only
        // collected the start date and auto-approve, and Autopilot owns both
        // (server derives start = tomorrow, approval is forced on).
        ...(autopilotPromo
            ? []
            : [
                  {
                      label: "Configure Campaign",
                      isActive: false,
                      isCompleted: false,
                  },
                  {
                      label: "Configure Schedule",
                      isActive: false,
                      isCompleted: false,
                  },
              ]),
    ];

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <ChoosePlatforms
                        setCurrentStep={setCurrentStep}
                        onNext={
                            autopilotPromo
                                ? () =>
                                      // MB-1421: one-step wizard — Autopilot
                                      // owns approval and start date, so Next
                                      // creates the campaign directly. Mirror
                                      // the manual promo flow, where paging
                                      // through the schedule step forces
                                      // soldPost off per platform.
                                      handleCreateCampaign(
                                          campaignPlatforms.map((p) => ({
                                              ...p,
                                              soldPost: false,
                                          }))
                                      )
                                : undefined
                        }
                        selectedPlatforms={selectedPlatforms}
                        setSelectedPlatforms={setSelectedPlatforms}
                        from={from}
                        listingId={listingId}
                        listingType={listingType}
                        instagramDisabled={instagramDisabled}
                        youtubeDisabled={youtubeDisabled}
                    />
                );
            case 2:
                progressSteps[0].isCompleted = true;
                // MB-1421: absent when Autopilot owns the promo flow and the
                // settings fetch resolved after the user reached this step —
                // same race the schedule step already guards below.
                if (progressSteps[1]) progressSteps[1].isActive = true;
                return (
                    <ChooseCampaignOptions
                        setCurrentStep={setCurrentStep}
                        autoApprove={autoApprove}
                        setAutoApprove={setAutoApprove}
                        autoGenerateSlideshow={autoGenerateSlideshow}
                        setAutoGenerateSlideshow={setAutoGenerateSlideshow}
                        autoVideoReady={autoVideoReady}
                        onAutoVideoConfigured={loadAutoVideoDefault}
                        listingType={listingType}
                        date={editCampaignId ? undefined : date}
                        setDate={editCampaignId ? undefined : setDate}
                        firstImage={firstImage}
                        setFirstImage={setFirstImage}
                        floorPlan={floorPlan}
                        setFloorPlan={setFloorPlan}
                        cta={cta}
                        setCta={setCta}
                        openHouseMonitoring={openHouseMonitoring}
                        setOpenHouseMonitoring={setOpenHouseMonitoring}
                    />
                );
            case 3:
                progressSteps[0].isCompleted = true;
                if (progressSteps[1]) {
                    progressSteps[1].isActive = true;
                    progressSteps[1].isCompleted = true;
                }
                // MB-1405: absent when Autopilot owns the schedule and the
                // settings fetch resolved after the user reached this step.
                if (progressSteps[2]) progressSteps[2].isActive = true;
                return (
                    <ChooseFrequency
                        setCurrentStep={setCurrentStep}
                        selectedPlatforms={selectedPlatforms}
                        campaignPlatforms={campaignPlatforms}
                        setCampaignPlatforms={setCampaignPlatforms}
                        listingType={listingType}
                        createCampaign={handleCreateCampaign}
                        occupiedSlotsMapping={getOccupiedSlotsByPlatform(
                            otherCampaignSettings
                        )}
                        instagramPostsUsedByOthers={
                            autopilotGovernsInstagram ? 0 : countInstagramPosts
                        }
                        getAvailableTimeSlot={findAvailableTimeSlot}
                        usableImageCount={usableImageCount}
                        isEditMode={Boolean(editCampaignId)}
                        sharedListing={sharedListing}
                    />
                );
            default:
                return (
                    <ChoosePlatforms
                        setCurrentStep={setCurrentStep}
                        selectedPlatforms={selectedPlatforms}
                        setSelectedPlatforms={setSelectedPlatforms}
                        from={from}
                        listingId={listingId}
                        listingType={listingType}
                        instagramDisabled={instagramDisabled}
                    />
                );
        }
    };

    const handleDiscardPendingEdit = async () => {
        if (!editCampaignId) return;
        setLoading(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            await axios.delete(
                `${backendUrl}/listing/campaign/${editCampaignId}/pending-settings`,
                { headers }
            );
            setLoading(false);
            sessionStorage.removeItem("editCampaignId");
            toast.success(
                <CustomToast
                    message="Pending changes discarded. This campaign keeps its current settings."
                    type="success"
                />,
                { autoClose: 4000 }
            );
            navigate(returnPath, {
                state: { listingId: listingId },
            });
        } catch (error) {
            setLoading(false);
            console.error("Failed to discard campaign settings:", error);
            toast.error(
                <CustomToast
                    message={getApiErrorMessage(
                        error,
                        "Failed to discard pending changes"
                    )}
                    type="error"
                />,
                { autoClose: 3000 }
            );
        }
    };

    const handleCreateCampaign = async (
        platformsOverride?: CampaignPlatform[]
    ) => {
        const platformsToSubmit = platformsOverride ?? campaignPlatforms;
        if (!listingId || !listingType) {
            window.scrollTo({ top: 0, behavior: "smooth" });
            toast.error(
                <CustomToast
                    message="Listing Id and/or Listing Type is not found. Try again from listings / promotions page."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
            return;
        }

        for (const platform of platformsToSubmit) {
            const footer = platform.footer || "";
            if (
                (platform.name === SocialPlatforms.Twitter &&
                    footer.length > 280) ||
                (platform.name !== SocialPlatforms.Twitter &&
                    footer.length > 300)
            ) {
                window.scrollTo({ top: 0, behavior: "smooth" });
                toast.error(
                    <CustomToast
                        message={`The footer for ${platform.name} exceeds the maximum length of 200 characters.`}
                        type="error"
                    />,
                    {
                        autoClose: 3000,
                    }
                );
                return;
            }
        }
        if (editCampaignId) {
            setLoading(true);
            try {
                const headers = await getAuthHeader();
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                await axios.post(
                    `${backendUrl}/listing/campaign/${editCampaignId}/pending-settings`,
                    {
                        platforms: platformsToSubmit,
                        autoApprove,
                        openHouseMonitoring,
                        firstImage,
                        floorPlan,
                        cta,
                        // MB-1314 (§O5): auto-video is MLS-only — mirrors the
                        // create payload so an edit can't opt a Rentcast
                        // campaign in.
                        autoGenerateSlideshow:
                            listingType === "MLSListing"
                                ? autoGenerateSlideshow
                                : false,
                    },
                    { headers }
                );
                setLoading(false);
                sessionStorage.removeItem("editCampaignId");
                toast.success(
                    <CustomToast
                        message={`Settings saved. They take effect at your next campaign renewal${
                            nextRenewalDate
                                ? ` on ${new Date(
                                      nextRenewalDate
                                  ).toLocaleDateString()}`
                                : ""
                        }. Existing scheduled posts are not changed.`}
                        type="success"
                    />,
                    { autoClose: 6000 }
                );
                navigate(returnPath, {
                    state: { listingId: listingId },
                });
            } catch (error) {
                setLoading(false);
                console.error("Failed to save campaign settings:", error);
                toast.error(
                    <CustomToast
                        message={getApiErrorMessage(
                            error,
                            "Failed to save campaign settings"
                        )}
                        type="error"
                    />,
                    { autoClose: 3000 }
                );
            }
            return;
        }
        const platforms = [];
        if (selectedPlatforms.facebook) platforms.push("Facebook");
        if (selectedPlatforms.instagram) platforms.push("Instagram");
        setLoading(true);
        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const url =
                listingType === "Promotion"
                    ? `${backendUrl}/promotions/campaign`
                    : `${backendUrl}/listing/campaign`;

            const payload = {
                listingId: listingId,
                listingType:
                    listingType === "MLSListing"
                        ? ListingType.MLSListing
                        : listingType === "Listing"
                        ? ListingType.RentcastListing
                        : null,
                platforms: platformsToSubmit,
                // MB-1405: tells the api to derive the schedule from Autopilot
                // (verified server-side; rejected when Autopilot is off).
                autopilotScheduled: autopilotPromo && listingType === "Promotion",
                // MB-1421: on the Autopilot promo path the server owns both —
                // start date derives to tomorrow (user tz, never same-day) and
                // approval is forced on. startDate is omitted entirely
                // (undefined keys drop out of the JSON body); autoApprove is
                // still sent, as true, to match what the server will do.
                startDate:
                    autopilotPromo && listingType === "Promotion"
                        ? undefined
                        : date,
                autoApprove:
                    autopilotPromo && listingType === "Promotion"
                        ? true
                        : autoApprove,
                // MB-1314 (§O5): auto-video is MLS-only — never opt a Rentcast
                // campaign in, even if the user's global default is on.
                autoGenerateSlideshow:
                    listingType === "MLSListing" ? autoGenerateSlideshow : false,
                firstImage: firstImage,
                floorPlan: floorPlan,
                cta: cta,
                openHouseMonitoring,
            };

            const response = await axios.post(url, payload, { headers });

            if (response.status === 201) {
                setLoading(false);
                toast.success(
                    <CustomToast
                        message="Campaign generation started!"
                        type="success"
                    />,
                    {
                        autoClose: 3000,
                    }
                );
                // clear session storage
                sessionStorage.removeItem("createCampaign");
                sessionStorage.removeItem("listingType");
                sessionStorage.removeItem("from");
                sessionStorage.removeItem("listingId");
                sessionStorage.removeItem("parsedFooter");
                sessionStorage.removeItem("parsedGoogleFooter");

                if (listingType === "Promotion") {
                    navigate("/promotions");
                } else {
                    navigate(`/listing?id=${listingId}`);
                }
            }
        } catch (error: unknown) {
            const errorResponse = (
                error as {
                    response?: { status?: number; data?: { reason?: string } };
                }
            ).response;
            if (errorResponse?.status === 410) {
                setLoading(false);
                setPlanLimitType(
                    errorResponse.data?.reason === "activeListingLimit"
                        ? "activeListingLimit"
                        : "campaign"
                );
                setPlanLimitOpen(true);
            } else {
                console.error(error);
                setLoading(false);
                toast.error(
                    <CustomToast
                        message={getApiErrorMessage(
                            error,
                            "An error occurred while creating the campaign. Please try again."
                        )}
                        type="error"
                    />,
                    {
                        autoClose: 3000,
                    }
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const isMobile = useIsMobile(900);

    if (!isOnline && !loading) {
        return <CampaignGenerationMaintenance />;
    }

    return (
        <main
            className={`flex flex-col justify-center items-center ${
                isMobile ? "bg-white" : "bg-background py-6 min-h-full"
            } shadow-[0px_4px_4px_rgba(0,0,0,0.25)] max-md:px-5`}
        >
            {loading && <LoadingScreen />}
            <section
                className={`flex flex-col items-center ${
                    isMobile ? "p-4 w-full" : "px-14 py-20 w-[905px]"
                } max-w-full bg-white rounded-2xl h-full max-md:px-5 max-md:pb-24 relative`}
            >
                <header className="flex flex-col mx-auto w-full max-md:max-w-full">
                    <h1 className="my-2 text-primary text-2xl font-bold leading-none text-center">
                        {editCampaignId
                            ? "Edit Campaign Settings"
                            : "Create a Campaign"}
                    </h1>
                    <StepProgressBar
                        steps={progressSteps.filter((step) => step)}
                    />
                </header>
                <div className="relative flex flex-col mx-auto mt-2 w-full max-w-full">
                    {editCampaignId && (
                        <div className="border border-[#ffbe00] bg-[#fff176]/30 rounded-md p-3 mb-4">
                            <p className="text-sm text-[#193456]">
                                <strong>Editing campaign settings.</strong>{" "}
                                Changes take effect at your next campaign
                                renewal
                                {nextRenewalDate
                                    ? ` on ${new Date(
                                          nextRenewalDate
                                      ).toLocaleDateString()}`
                                    : ""}
                                . Your existing scheduled posts will not be
                                changed or deleted.
                            </p>
                            {hasPendingEdit && (
                                <div className="mt-2 flex flex-col gap-2 min-[480px]:flex-row min-[480px]:items-center min-[480px]:justify-between">
                                    <p className="text-sm text-[#193456]">
                                        This campaign already has changes staged
                                        for the next renewal. Saving overwrites
                                        them; discarding reverts to its current
                                        settings.
                                    </p>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        onClick={handleDiscardPendingEdit}
                                        disabled={loading}
                                        className="shrink-0"
                                    >
                                        Discard pending changes
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                    {renderStep()}
                </div>
                {/* Plan limit popup using PopoverContent */}
                {planLimitOpen && (
                    <PlanLimitReached
                        open={planLimitOpen}
                        onClose={() => {
                            setPlanLimitOpen(false);
                            if (listingType === "Promotion") {
                                navigate("/promotions");
                            } else {
                                navigate(`/listing?id=${listingId}`);
                            }
                        }}
                        type={planLimitType}
                    />
                )}
            </section>
        </main>
    );
};

export default CreateCampaign;
